'use client';

import { useState } from 'react';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import HighlightOff from '@mui/icons-material/HighlightOff';
import Search from '@mui/icons-material/Search';
import { SxProps, Theme } from '@mui/material/styles';

interface SearchFieldProps {
	placeholderLabel: string;
	onSearch?: (searchValue: string) => void;
	onChange?: (value: string) => void;
	sx?: SxProps<Theme>;
}

const SearchField = ({ placeholderLabel, onSearch, onChange, sx }: SearchFieldProps) => {
	const [searchValue, setSearchValue] = useState('');

	const clearSearchField = () => {
		setSearchValue('');

		onChange?.('');
	};

	const handleSearch = () => {
		onSearch?.(searchValue);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			handleSearch();
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchValue(value);

		onChange?.(value);
	};

	return (
		<FormControl
			sx={{
				m: {
					xs: '8px auto',
					md: '0',
				},
				width: '100%',
				...sx,
			}}
			variant="outlined"
			size="small"
			data-testid="search-field"
		>
			<OutlinedInput
				id="search-input"
				type="search"
				value={searchValue}
				onChange={handleInputChange}
				onKeyDown={handleKeyDown}
				onFocus={() => onSearch && onSearch(searchValue)}
				placeholder={placeholderLabel}
				sx={{
					borderRadius: '50px',
					'& .MuiOutlinedInput-notchedOutline': {
						borderColor: 'grey.dark',
					},
					'&:hover .MuiOutlinedInput-notchedOutline': {
						borderColor: 'grey.dark',
					},
					'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
						borderColor: 'grey.dark',
					},
					color: 'white',
					'& .MuiInputBase-input': {
						color: 'black.main',
					},
					'& ::-webkit-search-cancel-button': {
						WebkitAppearance: 'none',
					},
					'& ::placeholder': {
						color: 'black.main',
						opacity: '1',
					},
				}}
				endAdornment={
					<InputAdornment position="end">
						{searchValue && (
							<IconButton onClick={clearSearchField} sx={{ color: 'black.main', mr: '2px' }} edge="end">
								<HighlightOff />
							</IconButton>
						)}
						<IconButton onClick={handleSearch} sx={{ color: 'black.main' }} edge="end">
							<Search />
						</IconButton>
					</InputAdornment>
				}
			/>
		</FormControl>
	);
};

export default SearchField;
